/**
 * Copy for the mobile stepped service-booking sheet (`ServiceBookingSheet`
 * and its steps). Kept in one module so a wording edit lands in a single
 * place across the sheet, the footer and the completed-step chips.
 *
 * The listing-agnostic chrome copy (`SHEET_BACK_ARIA_LABEL`,
 * `stepOfTotalLabel`, `changeStepChipLabel`, `NEXT_CTA_LABEL`) lives in
 * `steppedSheetCopy.ts` and is re-exported here for the service imports.
 */
// `guestsUnitsLine` is imported directly (not just re-exported below)
// because `timeUnitGuestsLine` further down this file calls it.
import { DEFAULT_DISPLAY_CURRENCY, formatCurrency } from '../utils/formatters';
import { pricingUnitNoun } from '../utils/pricingUnits';
import { guestsUnitsLine } from './steppedSheetCopy';

export {
  CHOOSE_AN_OPTION_LABEL,
  CHOOSE_REQUIRED_EXTRAS_LABEL,
  EXTRAS_HEADING,
  GUESTS_STEP_TITLE,
  NEXT_CTA_LABEL,
  OPTION_STEP_TITLE,
  REQUIRED_EXTRAS_HELPER_TEXT,
  SHEET_BACK_ARIA_LABEL,
  changeStepChipLabel,
  guestsUnitsLine,
  stepOfTotalLabel,
} from './steppedSheetCopy';

// Step titles. An Option/Route step's title depends on whether the listing
// carries an authored transfer hub, so only the titles used by every
// listing live here. A Direction step precedes Date and Time for a
// directional pick; Date is calendar-only, Time carries the chip/Hours
// controls — see `serviceSheetSteps.ts`. (`GUESTS_STEP_TITLE`,
// `OPTION_STEP_TITLE` and `EXTRAS_HEADING` now live in
// `steppedSheetCopy.ts`, re-exported above — stays use them too.)
export const ROUTE_STEP_TITLE = 'Route';
export const DIRECTION_STEP_TITLE = 'Direction';
export const DATE_STEP_TITLE = 'Date';
export const TIME_STEP_TITLE = 'Time';
// The final step's title when the sheet asks no guest count (services only;
// stays and packages keep GUESTS_STEP_TITLE).
export const DETAILS_STEP_TITLE = 'Details';

// Option/Route step copy.
export const CHOOSE_A_ROUTE_LABEL = 'Choose a route';
export const TRANSFERS_GROUP_LABEL = 'Transfers';
export const OTHER_OPTIONS_GROUP_LABEL = 'Other options';
export const FROM_PRICE_PREFIX = 'From';

// The option chip's direction words ("{option} · to Vail" / "· from Vail").
// Lowercase by design — the chip reads as a short summary line, unlike
// `TransferDirectionToggle`'s own pill labels ("To Vail"/"From Vail"),
// which are sentence-cased UI controls.
export const CHIP_TO_HUB_WORD = 'to';
export const CHIP_FROM_HUB_WORD = 'from';

export const PICK_A_DATE_LABEL = 'Pick a date';
export const PICK_A_TIME_LABEL = 'Pick a time';

/** Live-region status text shown next to the Extras heading (Guests step)
 *  and the footer total (both the stepped sheet and the Guests step) while
 *  a modifier-driven quote refetch is in flight. One wording, two mount
 *  points — never two independent copies. */
export const QUOTE_UPDATING_STATUS_TEXT = 'Updating…';

// The collapsed bar's *what*-line direction arrows ("{option} → {hub}" /
// "{option} ← {hub}") — distinct from the option chip's lowercase
// "to"/"from" wording (`CHIP_TO_HUB_WORD` / `CHIP_FROM_HUB_WORD`): the bar
// line is a compact single-line summary, the chip a sentence-style label.
export const BAR_TO_HUB_ARROW = '→';
export const BAR_FROM_HUB_ARROW = '←';

// Time-chip group label — follows the listing type, not the selected
// option: "Pick-up time" on a listing with a transfer hub; "Start time"
// otherwise.
export const PICKUP_TIME_LABEL = 'Pick-up time';
export const START_TIME_LABEL = 'Start time';

export const ADD_NOTE_LABEL = 'Add a note for your concierge';

// The Option/Route step's selected-row description expand/collapse control.
export const MORE_LABEL = 'More';
export const LESS_LABEL = 'Less';

// Guests step helper text — kept here (not inline in
// MobileServiceBooking/ServiceGuestsStep) so a copy edit lands in one file,
// matching the slot-window/time-unit-noun helpers already shared via
// `utils/serviceBookingInputs.ts`. (`guestsUnitsLine` lives in
// `steppedSheetCopy.ts`, re-exported above.)

/** "Up to N" — the non-vehicle Guests-stepper helper. */
export const guestsUpToHelper = (max: number): string => `Up to ${max}`;

/** "N vehicle(s) · up to M guests" — the vehicle-pricing Guests-stepper
 *  helper. */
export const guestsVehicleHelper = (vehicleCount: number, maxGuests: number): string =>
  `${vehicleCount} vehicle${vehicleCount !== 1 ? 's' : ''} · up to ${maxGuests} guests`;

/** "N guest(s) · M <unit>" — the footer units line for a time-unit
 *  service (hour/30min/day), naming the booked duration alongside the
 *  guest count. */
export const timeUnitGuestsLine = (
  guestCount: number,
  unitCount: number,
  unitNoun: string,
): string => `${guestsUnitsLine(guestCount)} · ${unitCount} ${unitNoun}`;

/** "M <unit>" — the footer units line for a time-unit service that asks no guest count. */
export const timeUnitsLine = (unitCount: number, unitNoun: string): string =>
  `${unitCount} ${unitNoun}`;

/** "N vehicle(s)" — the footer units line for vehicle pricing. */
export const vehicleUnitsLine = (vehicleCount: number): string =>
  `${vehicleCount} vehicle${vehicleCount !== 1 ? 's' : ''}`;

/** "$165.90 per person" — the rate line under a service's price. */
export const rateLine = (rate: number, unit: string | null | undefined): string =>
  `${formatCurrency(rate, DEFAULT_DISPLAY_CURRENCY, { alwaysCents: true })} per ${pricingUnitNoun(unit)}`;

/** "From $120 per hour" — the rate line before an option is picked. */
export const fromRateLine = (rate: number, unit: string | null | undefined): string =>
  `${FROM_PRICE_PREFIX} ${formatCurrency(rate)} per ${pricingUnitNoun(unit)}`;
